import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Banknote, Star, XCircle, PackageOpen, RotateCcw, MessageCircle, PackageSearch } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl, formatApiError, waLink, SAR } from "@/lib/api";
import { Loader, Empty } from "@/components/shared";
import { StatusBadge, PaymentBadge } from "@/lib/status";
import { ReturnBadge } from "@/lib/returns";
import { CustomerReturnDialog, ReturnCard } from "@/components/returns";
import { useSettings } from "@/context/SettingsContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function Orders() {
  const [orders, setOrders] = useState(null);
  const [returns, setReturns] = useState([]);
  const [reviewFor, setReviewFor] = useState(null);
  const [returnFor, setReturnFor] = useState(null);
  const [paying, setPaying] = useState(null);
  const s = useSettings();

  const load = () => {
    api.get("/orders/me").then(({ data }) => setOrders(data));
    api.get("/returns/me").then(({ data }) => setReturns(data));
  };
  useEffect(() => { load(); }, []);

  const cancel = async (id) => {
    if (!window.confirm("هل تريد إلغاء هذا الطلب؟")) return;
    try { await api.put(`/orders/${id}/status`, { status: "cancelled" }); toast.success("تم إلغاء الطلب"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const payNow = async (o) => {
    setPaying(o.id);
    try {
      const { data } = await api.post("/payments/tap/charge", { order_id: o.id });
      toast.info("جارٍ التحويل لبوابة الدفع...");
      window.location.assign(data.checkout_url);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setPaying(null); }
  };

  const support = (o) => {
    const link = waLink(s.support_whatsapp, `مرحبًا، أحتاج مساعدة من دعم منصة التشاليح.\nرقم الطلب: ${o.order_number}`);
    window.open(link, "_blank");
  };

  if (orders === null) return <Loader />;
  const returnByOrder = {};
  returns.forEach((r) => { if (!returnByOrder[r.order_id]) returnByOrder[r.order_id] = r; });
  const canReturn = (st) => ["accepted", "preparing", "ready", "delivering", "completed"].includes(st);

  return (
    <div className="mx-auto max-w-4xl px-4 sm:px-6 py-8">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
        <h1 className="font-head text-2xl sm:text-3xl font-black text-white">طلباتي</h1>
        <Link to="/my-requests" data-testid="orders-my-requests-link" className="inline-flex items-center gap-1.5 rounded-xl border border-[var(--amber)] px-4 py-2 text-sm font-bold text-[var(--amber)] hover:bg-[var(--amber)]/10 transition-colors">
          <PackageSearch size={16} /> طلبات القطع والعروض
        </Link>
      </div>
      {orders.length === 0 ? (
        <Empty text="لا توجد طلبات بعد." action={<Link to="/search" className="btn-amber">تصفح القطع</Link>} />
      ) : (
        <div className="space-y-4">
          {orders.map((o) => (
            <div key={o.id} data-testid={`order-${o.id}`} className="ts-card p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs text-[var(--text-muted)]">#{o.order_number}</span>
                <StatusBadge status={o.status} />
              </div>
              <div className="flex gap-3">
                {o.part_snapshot?.image && <img src={fileUrl(o.part_snapshot.image)} alt="" className="h-16 w-16 rounded-xl object-cover" />}
                <div className="flex-1">
                  <p className="font-bold text-white">{o.part_snapshot?.name}</p>
                  <p className="text-xs text-[var(--text-muted)]">{o.yard_name} · الكمية: {o.quantity}</p>
                  {o.payment_method === "online"
                    ? <div className="flex items-center gap-1 mt-1 text-xs text-[var(--text-muted)]">دفع إلكتروني</div>
                    : <div className="flex items-center gap-1 mt-1 text-xs text-[var(--emerald)]"><Banknote size={13} /> دفع عند الاستلام</div>}
                </div>
                <div className="text-left">
                  <p className="font-head text-lg font-black text-[var(--amber)]">{o.total.toLocaleString("ar")} {SAR}</p>
                  <p className="text-[10px] text-[var(--text-muted)]">{o.delivery_method === "delivery" ? "توصيل" : "استلام"}</p>
                </div>
              </div>
              <div className="mt-2"><PaymentBadge order={o} /></div>
              {o.payment_method === "online" && o.payment_status === "paid" && (
                <div data-testid={`receipt-${o.id}`} className="mt-2 rounded-xl bg-[var(--emerald)]/10 border border-[var(--emerald)]/30 px-3 py-2 text-xs text-[var(--emerald)]">
                  ✅ إيصال دفع إلكتروني — رقم العملية: <span className="font-mono font-bold">{o.tap_charge_id}</span>
                </div>
              )}
              <div className="flex flex-wrap gap-3 mt-3 pt-3 border-t items-center" style={{ borderColor: "var(--border-c)" }}>
                {o.payment_method === "online" && o.payment_status !== "paid" && !["cancelled", "rejected"].includes(o.status) && (
                  <button data-testid={`pay-order-${o.id}`} disabled={paying === o.id} onClick={() => payNow(o)} className="btn-amber !py-1.5 !px-3 text-xs disabled:opacity-60">
                    {o.payment_status === "failed" ? "إعادة محاولة الدفع" : "ادفع الآن"} · {o.total.toLocaleString("ar")} {SAR}
                  </button>
                )}
                {["new", "accepted", "preparing"].includes(o.status) && (
                  <button data-testid={`cancel-order-${o.id}`} onClick={() => cancel(o.id)} className="flex items-center gap-1 text-xs text-[#EF4444] hover:underline"><XCircle size={14} /> إلغاء الطلب</button>
                )}
                {o.status === "completed" && !returnByOrder[o.id] && (
                  <button data-testid={`review-order-${o.id}`} onClick={() => setReviewFor(o)} className="flex items-center gap-1 text-xs text-[var(--amber)] hover:underline"><Star size={14} /> قيّم التشليح</button>
                )}
                {canReturn(o.status) && !returnByOrder[o.id] && (
                  <button data-testid={`return-order-${o.id}`} onClick={() => setReturnFor(o)} className="flex items-center gap-1 text-xs text-white hover:text-[var(--amber)]"><RotateCcw size={14} /> طلب استرجاع / استبدال</button>
                )}
                <button data-testid={`support-order-${o.id}`} onClick={() => support(o)} className="flex items-center gap-1 text-xs text-[#25D366] hover:underline mr-auto"><MessageCircle size={14} /> تواصل مع دعم المنصة</button>
              </div>
              {returnByOrder[o.id] && (
                <div className="mt-3">
                  <ReturnCard req={returnByOrder[o.id]} role="customer" onUpdated={load} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {reviewFor && <ReviewDialog order={reviewFor} onClose={() => setReviewFor(null)} onDone={() => { setReviewFor(null); load(); }} />}
      {returnFor && <CustomerReturnDialog order={returnFor} onClose={() => setReturnFor(null)} onDone={() => { setReturnFor(null); load(); }} />}
    </div>
  );
}

function ReviewDialog({ order, onClose, onDone }) {
  const [stars, setStars] = useState(5);
  const [sq, setSq] = useState(5);
  const [speed, setSpeed] = useState(5);
  const [comment, setComment] = useState("");
  const submit = async () => {
    try {
      await api.post("/reviews", { order_id: order.id, stars, service_quality: sq, speed, comment });
      toast.success("شكرًا لتقييمك!"); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const Rate = ({ label, val, set, testid }) => (
    <div>
      <label className="text-xs font-bold text-[var(--text-muted)] block mb-1">{label}</label>
      <div className="flex gap-1" data-testid={testid}>
        {[1, 2, 3, 4, 5].map((i) => (
          <button key={i} onClick={() => set(i)}>
            <Star size={26} fill={i <= val ? "#F59E0B" : "none"} stroke="#F59E0B" />
          </button>
        ))}
      </div>
    </div>
  );
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[var(--bg-card)] border-[var(--border-c)] text-white max-w-md" dir="rtl">
        <DialogHeader><DialogTitle className="text-white text-right">تقييم {order.yard_name}</DialogTitle></DialogHeader>
        <div className="space-y-4 mt-2">
          <Rate label="التقييم العام" val={stars} set={setStars} testid="rate-overall" />
          <Rate label="جودة الخدمة" val={sq} set={setSq} testid="rate-quality" />
          <Rate label="سرعة التعامل" val={speed} set={setSpeed} testid="rate-speed" />
          <textarea placeholder="تعليقك (اختياري)" value={comment} onChange={(e) => setComment(e.target.value)} rows={3}
            className="w-full rounded-xl bg-[var(--bg-secondary)] px-3 py-2.5 outline-none resize-none" />
          <button data-testid="submit-review" onClick={submit} className="btn-amber w-full justify-center">إرسال التقييم</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
